import { useState } from 'react';
import { Plus, Trash2, Pencil, Users2, ChevronRight, UserCheck } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { ComissaoFormModal } from './ComissaoFormModal';
import { ComissaoChecklistSection } from '@/pages/eventos/components/ComissaoChecklistSection';

interface TabComissoesProps {
  eventoId?: string;
  campanhaId?: string;
}

export function TabComissoes({ eventoId, campanhaId }: TabComissoesProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const [isComissaoModalOpen, setIsComissaoModalOpen] = useState(false);
  const [editingComissaoId, setEditingComissaoId] = useState<string | undefined>();
  const [selectedComissaoId, setSelectedComissaoId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const utils = trpc.useUtils();

  const { data: comissoesData, isLoading } = trpc.comissoes.list.useQuery({
    eventoId,
    campanhaId,
    page,
    limit: 20,
  });
  const comissoes = comissoesData?.items;

  const deleteComissaoMutation = trpc.comissoes.delete.useMutation({
    onSuccess: () => {
      utils.comissoes.list.invalidate();
      if (eventoId) utils.eventos.getById.invalidate({ id: eventoId });
      if (selectedComissaoId) setSelectedComissaoId(null);
    },
    onError: (error) => alert('Erro ao remover: ' + error.message),
  });

  const removeMembroMutation = trpc.comissoes.removeMembro.useMutation({
    onSuccess: () => {
      if (selectedComissaoId) utils.comissoes.listMembros.invalidate({ comissaoId: selectedComissaoId });
      utils.comissoes.list.invalidate();
    },
    onError: (error) => alert('Erro ao remover: ' + error.message),
  });

  const handleEditComissao = (id: string) => {
    setEditingComissaoId(id);
    setIsComissaoModalOpen(true);
  };

  const handleNewComissao = () => {
    setEditingComissaoId(undefined);
    setIsComissaoModalOpen(true);
  };

  const handleDeleteComissao = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover esta comissao? Todos os membros e checklists serao removidos.')) {
      deleteComissaoMutation.mutate({ id });
    }
  };

  const handleRemoveMembro = (id: string) => {
    if (window.confirm('Tem certeza que deseja desvincular este membro?')) {
      removeMembroMutation.mutate({ id });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Comissoes Grid */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">
          {comissoes?.length || 0} comissao(oes)
        </h3>
        <Button onClick={handleNewComissao} size="sm" disabled={!canEdit}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Comissao
        </Button>
      </div>

      {comissoes && comissoes.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {comissoes.map((comissao) => (
            <button
              key={comissao.id}
              onClick={() => setSelectedComissaoId(
                selectedComissaoId === comissao.id ? null : comissao.id
              )}
              className={`relative rounded-lg border p-4 text-left transition-all ${
                selectedComissaoId === comissao.id
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{comissao.nome}</p>
                  {comissao.descricao && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{comissao.descricao}</p>
                  )}
                </div>
                <ChevronRight className={`h-4 w-4 text-gray-400 flex-shrink-0 ml-1 transition-transform ${
                  selectedComissaoId === comissao.id ? 'rotate-90' : ''
                }`} />
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-gray-500">
                  <Users2 className="inline h-3 w-3 mr-0.5" />
                  {comissao._count.membros} membros
                </span>
                <span className="text-xs text-gray-500">
                  {comissao._count.checklists} checklists
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Users2 className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p>Nenhuma comissao cadastrada</p>
            <p className="text-sm mt-1">Crie uma comissao para organizar membros e checklists</p>
          </CardContent>
        </Card>
      )}

      {comissoesData && <Pagination meta={comissoesData.meta} onPageChange={setPage} itemLabel="comissões" />}

      {/* Selected Comissao Details */}
      {selectedComissaoId && (
        <SelectedComissaoDetails
          comissaoId={selectedComissaoId}
          onEdit={() => handleEditComissao(selectedComissaoId)}
          onDelete={() => handleDeleteComissao(selectedComissaoId)}
          onRemoveMembro={handleRemoveMembro}
          removeMembroPending={removeMembroMutation.isPending}
          canEdit={canEdit}
        />
      )}

      {/* Comissao Modal */}
      <ComissaoFormModal
        isOpen={isComissaoModalOpen}
        onClose={() => { setIsComissaoModalOpen(false); setEditingComissaoId(undefined); }}
        eventoId={eventoId}
        campanhaId={campanhaId}
        comissaoId={editingComissaoId}
      />
    </div>
  );
}

// Sub-component for selected comissao details
interface SelectedComissaoDetailsProps {
  comissaoId: string;
  onEdit: () => void;
  onDelete: () => void;
  onRemoveMembro: (id: string) => void;
  removeMembroPending: boolean;
  canEdit: boolean;
}

function SelectedComissaoDetails({
  comissaoId,
  onEdit,
  onDelete,
  onRemoveMembro,
  removeMembroPending,
  canEdit,
}: SelectedComissaoDetailsProps) {
  const [membrosPage, setMembrosPage] = useState(1);
  const { data: membrosData, isLoading } = trpc.comissoes.listMembros.useQuery({ comissaoId, page: membrosPage, limit: 20 });
  const membros = membrosData?.items;

  return (
    <div className="space-y-4">
      {/* Comissao actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Membros</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onEdit} disabled={!canEdit}>
            <Pencil className="h-4 w-4 mr-1" />
            Editar Comissao
          </Button>
          <Button variant="danger" size="sm" onClick={onDelete} disabled={!canEdit}>
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      </div>

      {/* Members table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">{membrosData?.meta.total || 0} membro(s)</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
            </div>
          ) : membros && membros.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Funcao</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {membros.map((membro) => (
                  <TableRow key={membro.id}>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span title="Usuario do sistema"><UserCheck className="h-3.5 w-3.5 text-green-500" /></span>
                        <p className="font-medium">{membro.nome}</p>
                      </div>
                    </TableCell>
                    <TableCell>{membro.funcao}</TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{membro.userEmail || membro.email || '-'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveMembro(membro.id)}
                        disabled={!canEdit || removeMembroPending}
                        className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                        title="Desvincular"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-6 text-center text-gray-500">
              <Users2 className="mx-auto h-6 w-6 text-gray-300 mb-1" />
              <p className="text-sm">Nenhum membro nesta comissao</p>
              <p className="text-xs text-gray-400 mt-1">Edite a comissao para vincular membros</p>
            </div>
          )}
          {membrosData && (
            <div className="px-4 pb-2">
              <Pagination meta={membrosData.meta} onPageChange={setMembrosPage} itemLabel="membros" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Checklists Section */}
      <Card>
        <CardContent className="p-4">
          <ComissaoChecklistSection
            comissaoId={comissaoId}
            comissaoMembroId={membros?.[0]?.id}
          />
        </CardContent>
      </Card>
    </div>
  );
}
